import { useEffect, useRef, useState } from 'react';
import {
    Divider, FormControlLabel, FormGroup, FormHelperText, Grid, Typography,
} from '@mui/material';
import { Mail, Lock, Phone } from '@mui/icons-material';

import { useForm } from 'react-hook-form';
import TextField from '../../foundation/TextField';
import Button from '../../foundation/Button/Button';
import Anchor from '../../foundation/Anchor';
import { getParameterByName, tryNavigateToRedirectUrl } from '../../../utils/urlUtils';
import { getLoggedUser, registerUser } from '../../../api/auth.api';
import Alert from '../../foundation/Alert';
import { cellPhonePattern, emailPattern } from '../../../utils/forms.utils';
import GoogleLogin from '../../components/GoogleLogin';
import Checkbox from '../../foundation/Checkbox';
import type { LoggedInUser } from '../../../../types/auth.types';
import DialogWithHeader from '../../foundation/DialogWithHeader';
import CustomBR from '../../foundation/CustomBR';
import RegistrationTermsText from '../../components/RegistrationTermsText/RegistrationTermsText';
import AuthAudienceSelector from '../../components/AuthAudienceSelector';
import { ExpressOfferDialog, expressCvKey } from '../../components/ExpressDialogs/ExpressDialogs';
import { EXPRESS_VALIDITY_TEXT } from '../../../../common/expressConsent';

export type RegisterFormProps = {
    onRegistrationComplete?: (user: LoggedInUser) => void | Promise<void>
    welcomeText?: string
    showSmartAgent?: boolean
    isEmployer?: boolean
    initialUseEmailPassword?: boolean
    showPhone?: boolean
    showToEmployers?: boolean
    active?: boolean
};

export type RegisterModalProps = RegisterFormProps & {
    open: boolean
    onClose: () => void
    gotoLogin: () => void
};

interface IFormRegister {
    email: string;
    phone?: string;
    password: string;
    smartAgentByActivity: boolean
    showToEmployers: boolean
    termsAccepted: boolean
    coupon?: string
}

export const RegisterForm = ({
    onRegistrationComplete,
    welcomeText,
    showSmartAgent = true,
    isEmployer = false,
    initialUseEmailPassword = false,
    showPhone = true,
    showToEmployers,
    active = true,
}: RegisterFormProps): JSX.Element => {
    const [formErr, setFormErr] = useState<string>(null);
    const [useEmailPassword, setUseEmailPassword] = useState<boolean>(initialUseEmailPassword);
    const googleLoginRef = useRef<HTMLButtonElement>(null);
    const showMployExpressOption = showSmartAgent && !isEmployer && showToEmployers === undefined;
    const [expressOfferOpen, setExpressOfferOpen] = useState(false);
    const expressOfferSeen = useRef(false);
    const expressChoice = useRef<((proceed: boolean) => void) | null>(null);
    const submitting = useRef(false);
    const {
        register, handleSubmit, formState: { errors }, watch, trigger, setValue, getValues,
    } = useForm<IFormRegister>({ defaultValues: { showToEmployers: false } });

    const offerExpress = async () => {
        if (expressChoice.current) return false;
        if (!showMployExpressOption || getValues('showToEmployers') || expressOfferSeen.current) return true;
        expressOfferSeen.current = true;
        setExpressOfferOpen(true);
        return new Promise<boolean>(resolve => { expressChoice.current = resolve; });
    };

    const finishExpressChoice = (approved?: boolean) => {
        if (approved !== undefined) setValue('showToEmployers', approved);
        setExpressOfferOpen(false);
        expressChoice.current?.(approved !== undefined);
        expressChoice.current = null;
    };

    const rememberCvOffer = (user: LoggedInUser) => {
        if (!showMployExpressOption || !getValues('showToEmployers')) return;
        try { sessionStorage.setItem(expressCvKey(user.userid), '1'); } catch { /* Storage is optional. */ }
    };

    const onSubmit = async (data: IFormRegister) => {
        if (submitting.current) return;
        submitting.current = true;
        try {
            if (!(await offerExpress())) return;
            window.showBackdrop();
            const couponQueryString = getParameterByName('coupon');
            if (couponQueryString) {
                data.coupon = couponQueryString;
            }

            const result = await registerUser({
                ...data,
                isEmployer,
                smartAgentByActivity: showSmartAgent ? data.smartAgentByActivity : false,
                showToEmployers: showToEmployers
                    ?? (showMployExpressOption ? getValues('showToEmployers') : undefined),
            });
            rememberCvOffer(result.data);
            if (onRegistrationComplete) {
                await onRegistrationComplete(result.data);
            } else {
                tryNavigateToRedirectUrl();
            }

            if (window.location.pathname.indexOf('/buyJobs') !== -1) {
                window.location.assign('/backoffice/employers/manage-jobs');
            }
        } catch (error) {
            setFormErr(error.message);
        } finally {
            submitting.current = false;
            window.hideBackdrop();
        }
    };

    const handleGoogleAuthSuccess = async () => {
        try {
            const response = await getLoggedUser();
            if (response.data.newlyRegistered) rememberCvOffer(response.data);
            if (onRegistrationComplete) await onRegistrationComplete(response.data);
            else tryNavigateToRedirectUrl();
        } catch (error) {
            setFormErr(error.message);
        }
    };

    const emailInput = register('email', { required: useEmailPassword, pattern: emailPattern });
    const smartAgentInput = register('smartAgentByActivity');
    const mployExpressInput = register('showToEmployers');
    const termsAcceptedInput = register('termsAccepted', { required: true });
    const passwordInput = register('password', { required: useEmailPassword, minLength: 6 });
    const phoneInput = register('phone', { pattern: cellPhonePattern });

    useEffect(() => {
        if (active) setUseEmailPassword(initialUseEmailPassword);
        else {
            expressOfferSeen.current = false;
            setExpressOfferOpen(false);
            expressChoice.current?.(false);
            expressChoice.current = null;
        }
    }, [active, initialUseEmailPassword]);

    useEffect(() => () => expressChoice.current?.(false), []);

    return <div>
        <ExpressOfferDialog open={expressOfferOpen} registration
            onApprove={() => finishExpressChoice(true)} onRegular={() => finishExpressChoice(false)}
            onClose={() => finishExpressChoice()} />
        <span>{welcomeText}</span>
        {formErr && <><Alert severity="error">{formErr}</Alert><CustomBR /></>}
        <form onSubmit={useEmailPassword ? handleSubmit(onSubmit) : async (ev: React.FormEvent<HTMLFormElement>) => {
            ev.preventDefault();
            await trigger(showPhone ? ['phone', 'termsAccepted'] : ['termsAccepted']);
            googleLoginRef.current?.click();
        }}>
            {/* Phone field */}
            {showPhone && <><div>
                <TextField
                    {...phoneInput}
                    inputRef={phoneInput.ref}
                    margin="dense"
                    name="phone"
                    id="phone"
                    label="מספר טלפון"
                    type="tel"
                    autoComplete="phone"
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone && 'מספר הטלפון אינו תקין, לדוגמה: 0501234567 או 03-1234567'}
                    InputProps={{
                        endAdornment: <Phone />,
                    }}
                />
            </div>
            <CustomBR /></>}

            {!useEmailPassword && <>
                {/* Google Registration Form */}
                {/* Smart Agent Checkbox */}
                {showSmartAgent && <FormGroup>
                    <FormControlLabel
                        control={<Checkbox
                            defaultChecked
                            {...smartAgentInput}
                            inputRef={smartAgentInput.ref}
                            name="smartAgentByActivity"
                        />}
                        label="הפעל סוכן חכם מבוסס פעילות"
                    />
                    <FormHelperText>קבלת התראות על משרות חדשות מותאמות לפעילות שלכם</FormHelperText>
                </FormGroup>}

                {/* Mploy Express Checkbox */}
                {showMployExpressOption && <FormGroup>
                    <FormControlLabel
                        control={<Checkbox
                            {...mployExpressInput}
                            inputRef={mployExpressInput.ref}
                            name="showToEmployers"
                        />}
                        label="Mploy Express – הפצת קורות החיים למעסיקים"
                    />
                    <FormHelperText>
                            הפעלת השירות מהווה אישור ל־Mploy לשתף את קורות החיים שלך עם מעסיקים
                        <br />
                        {EXPRESS_VALIDITY_TEXT}
                    </FormHelperText>
                </FormGroup>}

                {/* Terms Checkbox */}
                <FormGroup>
                    <FormControlLabel
                        required
                        {...termsAcceptedInput}
                        inputRef={termsAcceptedInput.ref}
                        control={<Checkbox
                            name="termsAccepted"
                        />}
                        label="אישור התקנון ותנאי השימוש"
                    />
                    <FormHelperText>
                        <RegistrationTermsText />
                    </FormHelperText>
                    {errors.termsAccepted && <FormHelperText error>יש לאשר את התקנון ותנאי השימוש</FormHelperText>}
                </FormGroup>
                <CustomBR />

                <Grid container spacing={3}>
                    <Grid xs={12} item>
                        <GoogleLogin
                            ref={googleLoginRef}
                            flow="register"
                            onAuthSuccess={handleGoogleAuthSuccess}
                            isAllowedToOpenPopup={async () => {
                                const valid = await trigger(showPhone ? ['phone', 'termsAccepted'] : ['termsAccepted']);
                                if (!valid) return false;
                                return offerExpress();
                            }}
                            smartAgentByActivity={showSmartAgent ? watch('smartAgentByActivity', true) : false}
                            showToEmployers={showToEmployers
                                    ?? (showMployExpressOption ? watch('showToEmployers', false) : undefined)}
                            isEmployer={isEmployer}
                            phone={showPhone ? watch('phone', '') : undefined}
                        />
                    </Grid>
                </Grid>

                <CustomBR />
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item xs={5}><Divider /></Grid>
                    <Grid item><Typography variant="body1">או</Typography></Grid>
                    <Grid item xs={5}><Divider /></Grid>
                </Grid>
                <CustomBR />

                {/* Switch to Email/Password */}
                <Button
                    onClick={e => {
                        e.preventDefault();
                        setUseEmailPassword(true);
                    }}
                    variant="text"
                    fullWidth
                >
                        הרשמה עם מייל וסיסמא במקום
                </Button>
            </>}

            {useEmailPassword && <>
                {/* Email/Password Registration Form */}
                <div>
                    <TextField
                        required
                        {...emailInput}
                        inputRef={emailInput.ref}
                        margin="dense"
                        name="email"
                        id="email"
                        label="כתובת אימייל"
                        type="email"
                        autoComplete="username"
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email && 'כתובת המייל אינה תקינה'}
                        InputProps={{
                            endAdornment: <Mail />,
                        }}
                    />
                </div>
                <div>
                    <TextField
                        required
                        {...passwordInput}
                        inputRef={passwordInput.ref}
                        margin="dense"
                        name="password"
                        autoComplete="new-password"
                        id="password"
                        label="בחר סיסמא"
                        type="password"
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password && 'הסיסמא אינה תקינה, לפחות 6 תווים'}
                        InputProps={{
                            endAdornment: <Lock />,
                        }}
                    />
                </div>
                <CustomBR />

                {/* Smart Agent Checkbox */}
                {showSmartAgent && <FormGroup>
                    <FormControlLabel
                        control={<Checkbox
                            defaultChecked
                            {...smartAgentInput}
                            inputRef={smartAgentInput.ref}
                            name="smartAgentByActivity"
                        />}
                        label="הפעל סוכן חכם מבוסס פעילות"
                    />
                    <FormHelperText>קבלת התראות על משרות חדשות מותאמות לפעילות שלכם</FormHelperText>
                </FormGroup>}

                {/* Mploy Express Checkbox */}
                {showMployExpressOption && <FormGroup>
                    <FormControlLabel
                        control={<Checkbox
                            {...mployExpressInput}
                            inputRef={mployExpressInput.ref}
                            name="showToEmployers"
                        />}
                        label="Mploy Express – הפצת קורות החיים למעסיקים"
                    />
                    <FormHelperText>
                            הפעלת השירות מהווה אישור ל־Mploy לשתף את קורות החיים שלך עם מעסיקים
                        <br />
                        {EXPRESS_VALIDITY_TEXT}
                    </FormHelperText>
                </FormGroup>}

                {/* Terms Checkbox */}
                <FormGroup>
                    <FormControlLabel
                        required
                        {...termsAcceptedInput}
                        inputRef={termsAcceptedInput.ref}
                        control={<Checkbox
                            name="termsAccepted"
                        />}
                        label="אישור התקנון ותנאי השימוש"
                    />
                    <FormHelperText>
                        <RegistrationTermsText />
                    </FormHelperText>
                    {errors.termsAccepted && <FormHelperText error>יש לאשר את התקנון ותנאי השימוש</FormHelperText>}
                </FormGroup>
                <CustomBR />

                {/* Submit Button */}
                <Button onClick={handleSubmit(onSubmit)} color="primary" fullWidth>הרשמה</Button>

                <CustomBR />
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item xs={5}><Divider /></Grid>
                    <Grid item><Typography variant="body1">או</Typography></Grid>
                    <Grid item xs={5}><Divider /></Grid>
                </Grid>
                <CustomBR />

                {/* Switch back to Google */}
                <Button
                    onClick={e => {
                        e.preventDefault();
                        setUseEmailPassword(false);
                    }}
                    variant="text"
                    fullWidth
                >
                        חזרה להרשמה עם Google
                </Button>
            </>}
        </form>
    </div>;
};

const RegisterModal = ({
    open,
    onClose,
    gotoLogin,
    isEmployer = false,
    ...formProps
}: RegisterModalProps): JSX.Element => {
    const renderFooter = () => <Typography variant="body2">
        משתמש רשום? <Anchor text="לחץ כאן להתחברות" href="#" onClick={ev => {
            ev.preventDefault();
            gotoLogin();
        }} />
    </Typography>;

    return <DialogWithHeader
        renderFooter={renderFooter}
        open={open}
        onClose={onClose}
        title="הצטרפו ל-Mploy"
    >
        <>
            {!isEmployer && <AuthAudienceSelector onEmployerClick={() => window.location.assign('/backoffice/login')} />}
            <RegisterForm
                {...formProps}
                active={open}
                isEmployer={isEmployer}
            />
        </>
    </DialogWithHeader>;
};

export default RegisterModal;
